/**
 * What plays, when, and what comes after it.
 *
 * All the deciding and none of the sound: this file has never heard of an
 * AudioContext. It is here rather than in `platform/` because *which* track
 * follows *which* is the part with rules in it - one per map, some only in
 * 18+, one that jumps the queue when you come up on the кремль - and rules
 * are the part worth testing without a browser.
 *
 * The shape of it is a rotation with silence in it. A track plays, then
 * nothing plays for a minute or two, then another. The silence is not
 * economy: music that never stops stops being heard and starts being
 * noticed, and after an hour it is the thing somebody turns off. The gap is
 * also what lets the water and the вовкулака be heard, which are the sounds
 * this game actually has.
 *
 * A pleasant side effect: with a gap between tracks nothing has to loop
 * seamlessly, which is a real saving. An mp3 cannot loop without a gap
 * anyway - the encoder pads both ends of the stream - so a game that wanted
 * one continuous loop would need ogg and a composer willing to match the
 * ends exactly.
 */

/** Which map a track belongs to. `null` means any of them. */
export type MusicMap = "kremlin" | "hill" | "adventure" | null;

export interface Track {
  /** The file under `public/music/`, ASCII so no URL has to be escaped. */
  readonly file: string;
  /** What it is actually called, which is what a person should see. */
  readonly title: string;
  readonly map: MusicMap;
  /** Seconds. Only used to decide when the gap starts, so it may be rough. */
  readonly seconds: number;
  /**
   * Played only in 18+.
   *
   * Its own flag rather than a separate list, because a track is a track and
   * the only thing that differs is whether this world will have it.
   */
  readonly adultOnly?: boolean;
  /**
   * Not in the rotation: played when something happens.
   *
   * The кремль's was the first of these. A cue is not shuffled in with the
   * rest - it waits until it is called for, plays once, and then the
   * rotation carries on from where it was.
   *
   * "menu" is the odd one: it belongs to the home screen, which is not a
   * world and has no director. It is a cue only in the sense that matters
   * here - the rotation must never pick it.
   */
  readonly cue?: "kremlin" | "dragon" | "menu";
}

/**
 * Everything the game has.
 *
 * One list, because "which tracks exist" is a fact about the game and not
 * about any one map - and a list per map is three places to forget to add
 * something to.
 */
export const TRACKS: readonly Track[] = [
  /*
   * The home screen's own, which is not in any rotation.
   *
   * "Main Menu (The Steppe's Awakening) на головну сторінку." It starts on
   * the first click - a browser will not make an AudioContext before one -
   * and it stops the moment a world begins, because from then on the world
   * has its own.
   */
  {
    file: "main-menu-steppe-awakening.mp3",
    title: "Main Menu (The Steppe's Awakening)",
    map: null, seconds: 218, cue: "menu",
  },
  /*
   * The кремль, twice over: one for 18+ and one for everybody else.
   *
   * "Hostile Encounters (The Night Raid) при заході в кремль, для 18+ у нас
   * вже є, а ця буде для всіх інших." Which of the two plays is decided by
   * the world, not by this list - see `cueFor`.
   */
  {
    file: "kremlin-gate.mp3", title: "ЙБН РСН",
    map: "kremlin", seconds: 130, cue: "kremlin", adultOnly: true,
  },
  {
    file: "kremlin-night-raid.mp3",
    title: "Hostile Encounters (The Night Raid)",
    map: "kremlin", seconds: 133, cue: "kremlin",
  },
  /*
   * And the Змій, who is the other thing in this game that arrives.
   *
   * A cue rather than a track for the adventure map: it plays because
   * something is happening to you, and it stops being special the moment it
   * is in the rotation.
   */
  {
    file: "dragon-night-raid.mp3",
    title: "Hostile Encounters (The Night Raid) II",
    map: "adventure", seconds: 124, cue: "dragon",
  },

  /*
   * And the rest, which play everywhere.
   *
   * "Всі інші будуть по черзі грати по всіх локаціях, де не було вказано що
   * там є своя." Their names say Daytime, Caves, Village - and not one of
   * those is wired to anything yet, on purpose: the names are what they will
   * be for, and until each has a trigger they are the rotation. `map: null`
   * is what says so.
   */
  {
    file: "ingame-steppe-awakening.mp3",
    title: "In Game (The Steppe's Awakening)", map: null, seconds: 228,
  },
  {
    file: "dawn-sky-over-kyiv.mp3",
    title: "Dawn & Dusk (Sky over Kyiv)", map: null, seconds: 162,
  },
  {
    file: "dawn-sky-over-kyiv-2.mp3",
    title: "Dawn & Dusk (Sky over Kyiv) II", map: null, seconds: 163,
  },
  {
    file: "day-sunlight-on-the-wheat.mp3",
    title: "Daytime Overworld (Sunlight on the Wheat)",
    map: null, seconds: 193,
  },
  {
    file: "day-sunlight-on-the-wheat-2.mp3",
    title: "Daytime Overworld (Sunlight on the Wheat) II",
    map: null, seconds: 208,
  },
  {
    file: "night-shadows-of-the-forest.mp3",
    title: "Nighttime Overworld (Shadows of the Forest)",
    map: null, seconds: 181,
  },
  {
    file: "night-shadows-of-the-forest-2.mp3",
    title: "Nighttime Overworld (Shadows of the Forest) II",
    map: null, seconds: 209,
  },
  {
    file: "caves-the-chasm.mp3",
    title: "Deep Caves & Mining (The Chasm)", map: null, seconds: 210,
  },
  {
    file: "caves-the-chasm-2.mp3",
    title: "Deep Caves & Mining (The Chasm) II", map: null, seconds: 223,
  },
  {
    file: "village-cossack-camp.mp3",
    title: "Friendly Village (The Cossack Camp)", map: null, seconds: 149,
  },
  {
    file: "village-cossack-camp-2.mp3",
    title: "Friendly Village (The Cossack Camp) II", map: null, seconds: 123,
  },
];

/**
 * How long the quiet between two tracks lasts, in seconds.
 *
 * It was 60 to 120, and that is what "немає музики" was: a minute of silence
 * on walking into a world reads as a game with no music, and the only two
 * tracks anybody had heard were the two that start at once - the menu's and
 * the кремль's. Pottering about for the first minute is what everybody does
 * first, and it was scored with nothing.
 *
 * The quiet itself stays. Music that never stops stops being heard and
 * starts being noticed, and after an hour it is the thing somebody turns
 * off. Twenty-five to sixty seconds between tracks of two to four minutes
 * is still a game that breathes.
 */
export const GAP_SHORT = 25;
export const GAP_LONG = 60;

/**
 * And how long before the *first* one, which is a different question.
 *
 * Long enough not to tread on the menu's track as it fades, short enough
 * that walking into a world is scored. Nothing is downloaded until this
 * runs out, so it is also what keeps a dozen megabytes off the doorstep.
 */
export const GAP_FIRST = 6;

/**
 * How near the кремль you have to be for its track to start.
 *
 * Forty-five blocks, measured from the middle of the fortress, which is
 * about six outside its own wall - the кремль is sixty-one across. The point
 * is that it starts while you are still coming, not once you are inside:
 * this track is two minutes long and the walk in from the causeway is most
 * of that. Bound to the building rather than to a place on the map, because
 * which island the fortress stands on comes out of the seed and a coordinate
 * written down here would be right for exactly one world.
 */
export const KREMLIN_REACH = 45;

/**
 * And how near the Змій has to be for his.
 *
 * Shorter than the кремль's, because a fortress is a place you walk towards
 * and a dragon is a thing that arrives. Forty is about where he breaks off
 * circling his lair and comes at you - see `NOTICE` in core/entity/dragon.ts
 * - so the music starts as he turns, which is the moment worth scoring.
 */
export const DRAGON_REACH = 40;

export interface Playing {
  readonly track: Track;
  /** Seconds it has been playing. */
  readonly at: number;
}

/**
 * The thing that decides. Fed a clock and where the player is; answers with
 * what should be playing.
 */
export class Music {
  private playing: Playing | null = null;
  /** Seconds of quiet so far, and how much of it there is to be. */
  private quiet = 0;
  private quietFor: number;
  /** So the same track is never heard twice running. */
  private last: string | null = null;
  /**
   * Where each cue stands: near it, and whether it has been spent.
   *
   * Two maps rather than a pair of booleans per cue, because there are two
   * of these now and there will be more - the кремль and the Змій ask the
   * same three questions and answered them with copied code the first time
   * this was written for one of them.
   */
  private readonly inside = new Map<string, boolean>();
  private readonly spent = new Map<string, boolean>();
  /** Which track to play next whatever the rotation says. */
  private jumped: Track | null = null;

  constructor(
    private readonly map: MusicMap,
    private readonly adult: boolean,
    /** 0..1, chosen for each gap. Injected so a test can be sure of it. */
    private readonly roll: () => number = Math.random,
    private readonly tracks: readonly Track[] = TRACKS,
  ) {
    // A few seconds, not a minute: see GAP_FIRST.
    this.quietFor = GAP_FIRST;
  }

  /** What this map and this world may play, cues aside. */
  private rotation(): Track[] {
    return this.tracks.filter((t) =>
      t.cue === undefined &&
      (t.map === null || t.map === this.map) &&
      (t.adultOnly !== true || this.adult));
  }

  /**
   * The cue for a thing, given how far away this world's is.
   *
   * Two of them can be written for the same kind - the кремль has one for
   * 18+ and one for everybody else - so which one this world hears is
   * decided here rather than by the order of the list. A world with the
   * switch on takes the 18+ one if there is one; every other world takes the
   * first that is not.
   */
  private cueFor(kind: string): Track | undefined {
    const all = this.tracks.filter((t) => t.cue === kind);
    if (this.adult) {
      const grown = all.find((t) => t.adultOnly === true);
      if (grown !== undefined) return grown;
    }
    return all.find((t) => t.adultOnly !== true);
  }

  /**
   * Somebody is this far from the thing that has a cue.
   *
   * Called with the distance in blocks, or null on a map without one. The
   * cue fires on the way *in* and re-arms when they have left again, so
   * coming back a second time is a second arrival - and standing on the
   * threshold shuffling back and forth is not.
   */
  private approach(kind: string, distance: number | null, reach: number): void {
    if (distance === null) { this.inside.set(kind, false); return; }
    const was = this.inside.get(kind) === true;
    if (!was && distance <= reach) {
      this.inside.set(kind, true);
      if (this.spent.get(kind) !== true) {
        const cue = this.cueFor(kind);
        if (cue !== undefined) { this.jumped = cue; this.spent.set(kind, true); }
      }
    } else if (was && distance > reach * 1.6) {
      // Well outside before it counts as having left: a line you can stand
      // on is a line somebody walks over twice a second.
      this.inside.set(kind, false);
      this.spent.set(kind, false);
    }
  }

  nearKremlin(distance: number | null): void {
    this.approach("kremlin", distance, KREMLIN_REACH);
  }

  /**
   * And how near the nearest Змій is, or null when there is none about.
   *
   * His own cue rather than the кремль's: one is a place you arrive at and
   * the other is a thing that arrives at you, and they are not the same
   * feeling even when the tune is by the same hand.
   */
  nearDragon(distance: number | null): void {
    this.approach("dragon", distance, DRAGON_REACH);
  }

  /**
   * A second of the world going by.
   *
   * @returns the track to start now, or null for "carry on".
   */
  step(dt: number): Track | null {
    /*
     * A cue cuts whatever is playing, and that is the point of a cue.
     *
     * It used to wait its turn behind the rotation, which cost nothing while
     * a world opened with a minute of silence - you were always arriving
     * into quiet. Now that something is usually playing, waiting its turn
     * means a кремль track that starts four minutes after the кремль, which
     * is no cue at all.
     */
    if (this.jumped !== null && this.playing !== null) {
      this.last = this.playing.track.file;
      this.playing = null;
    }
    if (this.playing !== null) {
      const at = this.playing.at + dt;
      if (at < this.playing.track.seconds) {
        this.playing = { track: this.playing.track, at };
        return null;
      }
      this.last = this.playing.track.file;
      this.playing = null;
      this.quiet = 0;
      this.quietFor = GAP_SHORT + this.roll() * (GAP_LONG - GAP_SHORT);
    }

    // A cue does not wait for the gap to run out: the point of it is that it
    // starts when you arrive.
    if (this.jumped !== null) {
      const track = this.jumped;
      this.jumped = null;
      this.playing = { track, at: 0 };
      return track;
    }

    this.quiet += dt;
    if (this.quiet < this.quietFor) return null;

    const could = this.rotation();
    if (could.length === 0) return null;
    // Never the same one twice running, unless there is only one to play.
    const fresh = could.length > 1
      ? could.filter((t) => t.file !== this.last)
      : could;
    const pick = fresh[Math.min(fresh.length - 1,
      Math.floor(this.roll() * fresh.length))]!;
    this.playing = { track: pick, at: 0 };
    return pick;
  }

  /** What is playing, for anything that wants to show it. */
  get nowPlaying(): Track | null {
    return this.playing?.track ?? null;
  }

  /** Cut whatever is playing - going to the menu, or turning music off. */
  stop(): void {
    this.playing = null;
    this.quiet = 0;
    this.quietFor = GAP_FIRST;
  }
}
